import React, { useEffect, useState } from "react";
import Customer from "../Modals/Customer";
import Error from "../Components/Error";
import { useNavigate, useParams } from "react-router-dom";
import { FaRegUser } from "react-icons/fa";
import PhoneWithPass from "../Components/PhoneWithPass";
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";
import Spinner from "../Components/Spinner";
import { isAxiosError } from "axios";
import PassTemplate from "../Modals/PassTemplate";
import TransactionTable from "../Components/TransactionsTable";
import { useTranslation } from "react-i18next";
import { Bounce, toast } from "react-toastify";
import Pass from "../Modals/Pass";
import googleAdd from "../Images/Google.svg";
import { HiGift, HiSparkles, HiTrash, HiBellAlert, HiPlus, HiCheckCircle } from "react-icons/hi2";

const CustomerDetails: React.FC = () => {
  const [customer, setCustomer] = useState<Customer>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passTemplate, setPassTemplate] = useState<PassTemplate>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [pointsToAdd, setPointsToAdd] = useState(1);
  const { cid } = useParams<{ cid: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async (id: string) => {
      setLoading(true);
      setError("");
      try {
        const customerRes = await OrganizationAxiosInstance.get(`/Customer/${id}`);
        const customerData = customerRes.data;
        setCustomer(customerData);
        if (customerData.passTemplateId) {
          const tmplRes = await OrganizationAxiosInstance.get(`/PassTemplate/${customerData.passTemplateId}`);
          setPassTemplate(tmplRes.data);
        }
      } catch (e) {
        setError(isAxiosError(e) ? e.response?.data.error || "Error fetching data" : "Unexpected error");
      } finally {
        setLoading(false);
      }
    };
    if (cid) fetchAll(cid);
  }, [cid, refreshKey]);

  const calculateRewards = (pass: Pass | null | undefined) => {
    if (!pass) return { rewardsEarned: 0, rewardsAvailable: 0 };
    const rewardsEarned = Math.floor(pass.points / pass.pointsToCollect);
    return { rewardsEarned, rewardsAvailable: rewardsEarned - pass.radeemedRewards };
  };

  const { rewardsAvailable } = calculateRewards(customer?.pass);
  const hasReward = rewardsAvailable > 0;

  const addPoint = async () => {
    const toastId = toast.loading("Adding point…", { position: "top-right", theme: "light", transition: Bounce });
    try {
      await OrganizationAxiosInstance.put(`/Pass/${customer?.pass?.id}/${pointsToAdd}`);
      toast.update(toastId, { render: "Point added ✓", type: "success", isLoading: false, autoClose: 3000 });
      setRefreshKey(k => k + 1);
      setPointsToAdd(1);
    } catch (e) {
      toast.update(toastId, { render: isAxiosError(e) ? e.response?.data.error : "Error", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const sendMessage = async () => {
    setIsSending(true);
    const toastId = toast.loading(t("sending") as string, { position: "top-right", theme: "light", transition: Bounce });
    try {
      await OrganizationAxiosInstance.post(`/Pass/${customer?.pass?.id}/SendNotification/${message}`);
      toast.update(toastId, { render: t("messageSent") as string, type: "success", isLoading: false, autoClose: 3000 });
      setMessage("");
    } catch (e) {
      toast.update(toastId, { render: (isAxiosError(e) ? e.response?.data.error : "Error") as string, type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setIsSending(false);
    }
  };

  const redeemReward = async () => {
    const toastId = toast.loading("Redeeming reward…", { position: "top-right", theme: "light", transition: Bounce });
    try {
      await OrganizationAxiosInstance.put(`/Pass/${customer?.pass?.id}/RadeemReward/`);
      toast.update(toastId, { render: "Reward redeemed ✓", type: "success", isLoading: false, autoClose: 3000 });
      setRefreshKey(k => k + 1);
    } catch (e) {
      toast.update(toastId, { render: isAxiosError(e) ? e.response?.data.error : "Error", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const deleteUser = async () => {
    const toastId = toast.loading("Deleting customer…", { position: "top-right", theme: "light", transition: Bounce });
    try {
      await OrganizationAxiosInstance.delete(`/Customer/${customer?.id}`);
      toast.update(toastId, { render: "Customer deleted", type: "success", isLoading: false, autoClose: 3000 });
      navigate(`/Dashboard/Program/${customer?.passTemplateId}/Customers`);
    } catch (e) {
      toast.update(toastId, { render: isAxiosError(e) ? e.response?.data.error : "Error", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  if (loading) return <Spinner loading />;
  if (error) return <Error message={error} />;
  if (!passTemplate || !customer) return <></>;

  const pass = customer.pass;
  const pointsInCycle = pass && passTemplate.pointsToCollect > 0
    ? pass.points % passTemplate.pointsToCollect
    : 0;

  return (
    <div className="space-y-4 max-w-2xl mx-auto">

      {/* ── Customer header card ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
        {/* Top banner */}
        <div className="h-20 bg-gradient-to-r from-brand-100 to-brand-200" />

        <div className="px-5 pb-5 -mt-10">
          {/* Avatar */}
          <div className="w-16 h-16 rounded-2xl bg-white border-2 border-brand-100 shadow-sm flex items-center justify-center mb-3">
            <FaRegUser className="w-7 h-7 text-brand-500" />
          </div>

          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{customer.name}</h2>
              <p className="text-sm text-gray-500">{customer.phone}</p>
              {customer.email && <p className="text-sm text-gray-400">{customer.email}</p>}
              {customer.bDay && (
                <p className="text-xs text-gray-400 mt-0.5">🎂 {new Date(customer.bDay).toLocaleDateString()}</p>
              )}
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:bg-red-50 hover:text-red-500 transition"
            >
              <HiTrash className="w-5 h-5" />
            </button>
          </div>

          {/* Stat pills */}
          <div className="flex flex-wrap gap-2 mt-4">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 text-sm font-semibold">
              <HiSparkles className="w-4 h-4" />
              {pass?.points ?? 0} {t("points")}
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-sm font-semibold">
              <HiGift className="w-4 h-4" />
              {rewardsAvailable} {t("rewards")}
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${
              customer.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"
            }`}>
              <HiCheckCircle className="w-4 h-4" />
              {customer.active ? t("active") : t("inActive")}
            </div>
          </div>
        </div>
      </div>

      {/* ── Loyalty card + actions ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row gap-6 items-center">
          {/* Phone preview */}
          <div className="flex-shrink-0">
            <PhoneWithPass
              backgroundColor={passTemplate.backgroundColor}
              logoUrl={passTemplate.logo}
              pointImageUrl={passTemplate.pointImage}
              stripBackgroundColor={passTemplate.stripeBackgroundColor}
              stripBackgroundUrl={passTemplate.stripeBackgroundImage}
              textColor={passTemplate.textColor}
              pointsToCollect={passTemplate.pointsToCollect}
              points={pass?.points ?? 0}
              radeemedRewards={pass?.radeemedRewards ?? 0}
            />
          </div>

          {/* Actions */}
          <div className="flex-1 w-full space-y-3">
            {/* Progress to next reward */}
            {passTemplate.pointsToCollect > 0 && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{t("points")}</span>
                  <span>{pointsInCycle} / {passTemplate.pointsToCollect}</span>
                </div>
                <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-400 transition-all"
                    style={{ width: `${Math.min(100, (pointsInCycle / passTemplate.pointsToCollect) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              <input
                type="number"
                min={1}
                value={pointsToAdd}
                onChange={e => setPointsToAdd(Math.max(1, Number(e.target.value)))}
                className="w-20 px-3 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 text-center focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition"
              />
              <button
                onClick={addPoint}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 transition active:scale-95"
              >
                <HiPlus className="w-4 h-4" /> Add Point{pointsToAdd > 1 ? `s (${pointsToAdd})` : ""}
              </button>
            </div>

            <button
              onClick={redeemReward}
              disabled={!hasReward}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed bg-emerald-500 text-white hover:bg-emerald-600"
            >
              <HiGift className="w-4 h-4" />
              {hasReward ? t("Radeem Reward") : t("No Rewards")}
            </button>

            {/* Wallet buttons */}
            <div className="flex gap-3 pt-1">
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://pay.google.com/gp/v/save/${pass?.googleJwtToken}`}
                className="flex-1 flex justify-center"
              >
                <img src={googleAdd} alt="Add to Google Wallet" className="h-9" />
              </a>
              <a
                target="_blank"
                rel="noreferrer"
                href={`${process.env.REACT_APP_HOST}Pass/GeneratePassApple/${customer.id}`}
                className="flex-1 flex justify-center"
              >
                <img
                  src="https://cdsassets.apple.com/live/7WUAS350/images/ios/add-to-apple-wallet-logo.png"
                  alt="Add to Apple Wallet"
                  className="h-9"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Send notification ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <HiBellAlert className="w-4 h-4 text-brand-600" />
          <h3 className="text-sm font-bold text-gray-900">{t("sendNotification")}</h3>
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={3}
          placeholder={t("yourMessage")}
          className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition resize-none"
        />
        <button
          onClick={sendMessage}
          disabled={!message.trim() || isSending}
          className="w-full py-2.5 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed transition active:scale-95"
        >
          {isSending ? t("sending") : t("sendMessage")}
        </button>
      </div>

      {/* ── Transactions ── */}
      <div className="bg-white rounded-2xl border border-brand-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">{t("recentTransactions")}</h3>
        </div>
        <div className="overflow-x-auto">
          <TransactionTable passTemplateId={passTemplate.id} customerId={cid} />
        </div>
      </div>

      {/* ── Delete confirmation modal ── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <HiTrash className="w-7 h-7 text-red-500" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Delete Customer?</h3>
              <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => { deleteUser(); setShowDeleteModal(false); }}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetails;
